// ── Boutons d'actions par champ — Blueprint & DocForge ──
// C.FIELD.ACTIONS.1 — S158/S159
// C.FIELD.ASSIGN.1 — S166
// Pattern: ✏️ Modifier + 🎯 Challenger (bot-aware) + 🧑 Assigner (task) + skills spécialisés par bot
import { Pencil, Target, UserPlus } from "lucide-react";
import { cn } from "../../components/ui/utils";
import {
  getSkillActions,
  buildChallengerPrompt,
  type ActionContext,
} from "./field-actions-config";
import { useAmorcer } from "../AmorcerContext";

const AUTONOMY_LABEL: Record<string, string> = {
  observer: "N1",
  copilot: "N2",
  autopilot: "N3",
};

const AUTONOMY_TITLE: Record<string, string> = {
  observer: "Suggérer (N1)",
  copilot: "Rédiger (N2)",
  autopilot: "Autonome (N3)",
};

interface FieldActionButtonsProps {
  fieldId: string;
  fieldLabel: string;
  fieldValue: string;
  sectionId: string;
  botCode: string;
  autonomyLevel?: "observer" | "copilot" | "autopilot";
  onSendPrompt: (prompt: string) => void;
  onEditInline: () => void;
}

export function FieldActionButtons({
  fieldId,
  fieldLabel,
  fieldValue,
  sectionId,
  botCode,
  autonomyLevel = "copilot",
  onSendPrompt,
  onEditInline,
}: FieldActionButtonsProps) {
  const { addTask } = useAmorcer();
  const isEmpty = !fieldValue.trim();
  const autonomyTag = AUTONOMY_LABEL[autonomyLevel] ?? "N2";
  const autonomyTitle = AUTONOMY_TITLE[autonomyLevel] ?? "Rédiger (N2)";

  const ctx: ActionContext = {
    fieldLabel,
    fieldValue,
    sectionId,
    botCode,
    autonomyLevel,
    autonomyTitle,
  };

  const skillActions = getSkillActions(botCode);

  const btnBase = cn(
    "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium",
    "border transition-colors cursor-pointer select-none"
  );

  return (
    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
      {/* ✏️ Modifier — toujours visible, édition inline directe */}
      <button
        type="button"
        onClick={onEditInline}
        className={cn(btnBase, "border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700")}
        title="Modifier ce champ"
      >
        <Pencil className="h-2.5 w-2.5" />
        Modifier
      </button>

      {/* 🎯 Challenger — toujours visible, prompt expert par bot */}
      <button
        type="button"
        onClick={() => onSendPrompt(buildChallengerPrompt(ctx))}
        className={cn(btnBase, "border-orange-200 text-orange-600 hover:bg-orange-50")}
        title="Challenger cette valeur (expert)"
      >
        <Target className="h-2.5 w-2.5" />
        Challenger
      </button>

      {/* 🧑 Assigner — délègue le champ comme tâche N1 (observer) */}
      <button
        type="button"
        onClick={() =>
          addTask({
            botCode,
            label: `Assigner: ${fieldLabel}`,
            prompt: fieldValue || `Traiter le champ "${fieldLabel}"`,
            autonomyLevel: "observer",
            sourceContext: { surface: "field", fieldId, sectionId },
          })
        }
        className={cn(btnBase, "border-blue-200 text-blue-600 hover:bg-blue-50")}
        title="Assigner ce champ comme tâche (N1 — suggérer)"
      >
        <UserPlus className="h-2.5 w-2.5" />
        Assigner
      </button>

      {/* Bot-specific skill actions — exploitent les capacités OpenClaw du bot */}
      {skillActions.map((action) => {
        const Icon = action.icon;
        const disabled = !!action.requiresValue && isEmpty;
        return (
          <button
            key={action.id}
            type="button"
            disabled={disabled}
            onClick={() => {
              if (!disabled) onSendPrompt(action.buildPrompt(ctx));
            }}
            className={cn(
              btnBase,
              disabled
                ? "border-gray-100 text-gray-300 cursor-not-allowed"
                : action.colorClass
            )}
            title={
              disabled
                ? "Champ vide — saisir une valeur d'abord"
                : action.label
            }
          >
            <Icon className="h-2.5 w-2.5" />
            {action.label}
            {action.autonomyBadge && !disabled && (
              <span
                className={cn(
                  "ml-0.5 text-[9px] font-bold px-0.5 rounded opacity-70"
                )}
              >
                {autonomyTag}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
